import { AfterViewInit, Component, inject, OnInit } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { TrackRender } from 'app/shared/front-perfomance';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { catchError, combineLatest, forkJoin, of, take } from 'rxjs';
import { AuthRepository } from 'app/state/auth.repository';
import { GeneralDataRepository } from 'app/helpers/repository/general-data.repository';
import { TenantFeatures } from 'app/state/feature.repository';
import { LocalStorageService } from 'app/api/services/local-storage-service';
import { TenantsService } from 'app/state/tenants.service';
import { TenantHomeUpdateModel } from 'app/state/tenants.repository';
import { EventService } from 'app/state/event.service';
import { PersonalTaskService } from 'app/state/personal-task.service';
import { PostsService } from 'app/state/posts.service';
import { ToolBoxesService } from 'app/api/services/toolbox.service';
import { SprintService } from 'app/api/services/sprint.service';
import { NewsUserPageListDto } from 'app/api/models/news/newsUserPageListDto';
import { ToolboxForListDto } from 'app/api/models/toolbox/toolboxForListDto';
import { IEvent } from 'app/api/models/calendar/eventDto';
import { PersonalTaskDto } from 'app/api/models/calendar/personalTaskDto';
import { SprintListDto } from 'app/api/models/sprint/sprintListDto';
import { AgendaItemView } from './dashboard/today-agenda.component';
import { TaskRowView } from './dashboard/tasks-summary.component';
import { LearningProgress } from './dashboard/continue-learning.component';

@UntilDestroy()
@TrackRender()
@Component({
  selector: 'app-homepage',
  templateUrl: './homepage.component.html',
  styleUrls: ['./homepage.component.scss'],
  standalone: false,
})
export class HomepageComponent implements OnInit, AfterViewInit {
  firstName: string | null = null;
  loading = true;

  // True when the tenant gates on Billing but the user has no active plan
  // (no plan-granted feature beyond the always-available Billing entry). The
  // home body is then replaced with a paywall lock prompting them to subscribe.
  lockedByPaywall = false;

  // Nybolig gets a launcher-style menu grid instead of the default dashboard,
  // on both mobile and desktop.
  isNybolig = false;
  // Gates the home body until the active tenant has been matched against the
  // tenant-options list. Without this, the default dashboard (the @else branch)
  // paints for one tick before isNybolig flips true, causing a visible blink of
  // the wrong content on Nybolig (and any tenant-specific layout).
  tenantResolved = false;
  private dashboardLoaded = false;

  // Tenant "Information" tab content (headline / pageHtml / video / pageHtml2 /
  // mobile description). Rendered above the grid/dashboard, restoring what the
  // old home page (menu.component) used to show. Loaded per active tenant.
  homeInfo: TenantHomeUpdateModel | null = null;
  videoUrlSafe: SafeResourceUrl | null = null;
  private homeInfoTenantId: string | null = null;

  get hasHomeInfo(): boolean {
    const m = this.homeInfo;
    return !!(
      m &&
      (m.headline ||
        m.pageHtml ||
        m.pageHtml2 ||
        m.descriptionMobile ||
        m.videoUrl ||
        m.logo)
    );
  }

  /** Mobile only shows the headline, video and the dedicated "Mobile
   *  Description" field (pageHtml / pageHtml2 are desktop-only). Used to hide
   *  the whole info card on mobile when none of those are set, so an empty
   *  bordered box isn't left behind. */
  get hasMobileHomeInfo(): boolean {
    const m = this.homeInfo;
    return !!(m && (m.headline || m.descriptionMobile || m.videoUrl || m.logo));
  }

  agenda: AgendaItemView[] = [];
  tasks: TaskRowView[] = [];
  news: NewsUserPageListDto[] = [];
  tools: ToolboxForListDto[] = [];
  learning: LearningProgress | null = null;

  // Features enabled for the active tenant — used to hide dashboard sections
  // (and the matching hero quick-actions) for features the tenant doesn't have.
  enabledFeatures: TenantFeatures[] = [];

  get hasSprints(): boolean {
    return this.enabledFeatures.includes(TenantFeatures.Sprints);
  }
  get hasToolbox(): boolean {
    return this.enabledFeatures.includes(TenantFeatures.ToolBox);
  }
  get hasNews(): boolean {
    return this.enabledFeatures.includes(TenantFeatures.News);
  }
  get hasCalendar(): boolean {
    return this.enabledFeatures.includes(TenantFeatures.Calendar);
  }
  get hasTodo(): boolean {
    return this.enabledFeatures.includes(TenantFeatures.Todo);
  }
  /** Left column (learning track / toolboxes / news). */
  get showLeftColumn(): boolean {
    return this.hasSprints || this.hasToolbox || this.hasNews;
  }
  /** Right column (today's calendar / other tasks). */
  get showRightColumn(): boolean {
    return this.hasCalendar || this.hasTodo;
  }

  // Aggregate counts surfaced in the hero chips.
  meetingsCount = 0;
  learningPct = 0;

  private readonly authRepo = inject(AuthRepository);
  private readonly events = inject(EventService);
  private readonly personalTasks = inject(PersonalTaskService);
  private readonly posts = inject(PostsService);
  private readonly toolboxes = inject(ToolBoxesService);
  private readonly sprints = inject(SprintService);
  private readonly generalDataRepo = inject(GeneralDataRepository);
  private readonly storage = inject(LocalStorageService);
  private readonly tenantsService = inject(TenantsService);
  private readonly sanitizer = inject(DomSanitizer);

  ngOnInit(): void {
    this.authRepo.name$.pipe(untilDestroyed(this)).subscribe((name) => {
      if (name) {
        this.firstName = name.split(/\s+/)[0];
      }
    });

    this.generalDataRepo.activeTenantFeatures$
      .pipe(untilDestroyed(this))
      .subscribe((features) => {
        this.enabledFeatures = features ?? [];
      });

    this.generalDataRepo.allGeneralData$
      .pipe(untilDestroyed(this))
      .subscribe((data) => {
        const tenantFeatures = data.features ?? [];
        const userFeatures = data.featuresForUser ?? [];
        const billingEnabled = tenantFeatures.includes(TenantFeatures.Billing);
        // Only the Billing entry is auto-granted, so "no other feature" == no active plan.
        const hasPlanAccess = userFeatures.some(
          (f) => f !== TenantFeatures.Billing,
        );
        this.lockedByPaywall = billingEnabled && !hasPlanAccess;
      });

    combineLatest([
      this.generalDataRepo.tenantsOptions$,
      this.storage.getTenantId$(),
    ])
      .pipe(untilDestroyed(this))
      .subscribe(([options, id]) => {
        // Home Information content depends only on the active tenant id, not on
        // the tenant-options list — load it before the early-return guard so it
        // isn't skipped on the first tick when options haven't arrived yet.
        if (id) {
          this.loadHomeInfo(id);
        }

        if (!options.length) return;
        const match = options.find((o) => o.value === id);
        this.isNybolig = (match?.label ?? '').toLowerCase().includes('nybolig');
        this.tenantResolved = true;

        // The default dashboard aggregates several endpoints; skip that work
        // entirely when showing the Nybolig launcher grid.
        if (!this.isNybolig && !this.dashboardLoaded) {
          this.dashboardLoaded = true;
          this.loadDashboard();
        }
      });
  }

  private loadHomeInfo(tenantId: string): void {
    if (this.homeInfoTenantId === tenantId) return;
    this.homeInfoTenantId = tenantId;
    this.tenantsService
      .loadTenantHomePageModel(tenantId)
      .pipe(
        catchError(() => of(null)),
        untilDestroyed(this),
      )
      .subscribe((model) => {
        this.homeInfo = model;
        this.videoUrlSafe = model?.videoUrl
          ? this.sanitizer.bypassSecurityTrustResourceUrl(model.videoUrl)
          : null;
      });
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      console.log('APP_READY_HomePage');
    }, 1000);
  }

  private loadDashboard(): void {
    const today = new Date();
    const dateStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

    forkJoin({
      events: this.events
        .getByDate(dateStr)
        .pipe(catchError(() => of([] as IEvent[]))),
      tasks: this.personalTasks
        .getMyTasks()
        .pipe(catchError(() => of([] as PersonalTaskDto[]))),
      news: this.posts
        .getNewsUserPage()
        .pipe(catchError(() => of([] as NewsUserPageListDto[]))),
      tools: this.toolboxes
        .loadAll()
        .pipe(catchError(() => of([] as ToolboxForListDto[]))),
      sprints: this.sprints
        .loadAllForUser()
        .pipe(catchError(() => of([] as SprintListDto[]))),
    })
      .pipe(take(1), untilDestroyed(this))
      .subscribe({
        next: ({ events, tasks, news, tools, sprints }) => {
          this.agenda = this.mapAgenda(events);
          this.tasks = this.mapTasks(tasks);
          this.news = news ?? [];
          this.tools = tools ?? [];
          this.learning = this.mapLearning(sprints);
          this.meetingsCount = this.agenda.length;
          this.learningPct = this.learning?.percentComplete ?? 0;
          this.loading = false;
        },
        error: () => {
          this.loading = false;
        },
      });
  }

  private mapAgenda(events: IEvent[]): AgendaItemView[] {
    if (!Array.isArray(events)) return [];
    const rows = events.map((e) => {
      const start = new Date(e.startDate ?? e.eventDate);
      const valid = !Number.isNaN(start.getTime());
      return {
        ts: valid ? start.getTime() : 0,
        view: {
          id: String(e.id ?? ''),
          time: valid
            ? start.toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit',
              })
            : '',
          title: String(e.title ?? ''),
          contact: e.crmContactName ?? e.crmAccountName ?? null,
          status: 'in' as const,
          value: null,
        } satisfies AgendaItemView,
      };
    });
    rows.sort((a, b) => a.ts - b.ts);
    return rows.map((r) => r.view);
  }

  private mapTasks(tasks: PersonalTaskDto[]): TaskRowView[] {
    if (!Array.isArray(tasks)) return [];
    const open = tasks.filter((t) => !t.isCompleted);
    if (open.length === 0) return [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayCount = open.filter((t) => {
      const due = new Date(t.dueDate);
      return (
        !Number.isNaN(due.getTime()) &&
        due.getTime() <= today.getTime() + 24 * 60 * 60 * 1000
      );
    }).length;
    const upcomingCount = open.length - todayCount;

    const rows: TaskRowView[] = [];
    if (todayCount > 0) {
      rows.push({
        kind: 'today',
        labelKey: 'dashTaskToday',
        count: todayCount,
        subKey: 'dashTaskTodaySub',
        icon: 'check',
        accent: 'var(--igntr-accent)',
        link: '/todo',
      });
    }
    if (upcomingCount > 0) {
      rows.push({
        kind: 'upcoming',
        labelKey: 'dashTaskUpcoming',
        count: upcomingCount,
        subKey: 'dashTaskUpcomingSub',
        icon: 'calendar',
        accent: 'oklch(0.62 0.16 160)',
        link: '/todo',
      });
    }
    return rows;
  }

  private mapLearning(sprints: SprintListDto[]): LearningProgress | null {
    if (!Array.isArray(sprints) || sprints.length === 0) return null;

    const myActive = sprints
      .filter((s) => s.isActive && Array.isArray(s.participants))
      .map((s) => {
        const me = s.participants.find((p) => p.isCurrentUser);
        return me ? { sprint: s, progression: me.progression ?? -1 } : null;
      })
      .filter(
        (x): x is { sprint: SprintListDto; progression: number } => x !== null,
      );

    if (myActive.length === 0) return null;

    // Prefer an in-progress sprint (started but not finished), else the most recently updated one.
    const inProgress = myActive.find(
      (x) => x.progression >= 0 && x.progression < x.sprint.modulesCount - 1,
    );
    const pick = inProgress ?? myActive[0];

    // progression matches sprint-pipeline-view semantics: -1 = not started,
    // 0..N-2 = in progress (index of current module), N-1 = completed.
    const total = Math.max(1, pick.sprint.modulesCount ?? 0);
    const prog = pick.progression;
    const isComplete = prog >= total - 1;
    const currentStep = Math.min(Math.max(0, prog), total - 1);
    const completedCount = isComplete ? total : Math.max(0, prog);
    const percentComplete = Math.min(
      100,
      Math.round((completedCount / total) * 100),
    );

    return {
      title: '',
      totalSteps: total,
      currentStep,
      currentStepTitle: String(pick.sprint.title ?? ''),
      resumeLink: `/sprints/${pick.sprint.id}`,
      percentComplete,
    };
  }
}
